'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { DataCell } from '@/components/data-cell';
import { HowItWorksInfoButton } from '@/components/how-it-works/how-it-works-info-button';
import { ProposalRatingHowItWorksModal } from '@/components/how-it-works/proposal-rating-how-it-works-modal';
import type { ProposalScore } from '@/components/launchpad/proposal-score-badge';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { AngryIcon, NeutralIcon, Relieved01Icon } from '@/components/ui/icon/glyphs';
export interface ProposalSummaryCellsProps {
  /** Backend-computed quality bucket — see backend
   *  `computeProposalScore` (weighted sum of nine signals). */
  score: ProposalScore;
  className?: string;
}
const SCORE_DESCRIPTOR: Record<ProposalScore, {
  icon: IconSvgElement;
  tone: string;
}> = {
  good: {
    icon: Relieved01Icon,
    tone: 'text-rating-good'
  },
  medium: {
    icon: NeutralIcon,
    tone: 'text-rating-medium'
  },
  bad: {
    icon: AngryIcon,
    tone: 'text-rating-bad'
  }
};

/**
 * Quality-of-creation rating cell on the proposal detail page. The
 * sibling status cell was removed — status is already conveyed by
 * `<ProposalStatusBadge>` on the hero (and via the funding-progress
 * bar's tick/colour), so the duplicate cell here was noise.
 */
export function ProposalSummaryCells(t0) {
  const $ = _c(24);
  const {
    score,
    className
  } = t0;
  const tDetail = useTranslations("launchpad.detail");
  const tScore = useTranslations("launchpad.feed.score");
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const scoreCfg = SCORE_DESCRIPTOR[score];
  let t1;
  if ($[0] !== scoreCfg.icon) {
    t1 = <Icon icon={scoreCfg.icon} />;
    $[0] = scoreCfg.icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = scoreCfg.tone;
  let t3;
  if ($[2] !== tDetail) {
    t3 = tDetail("summaryRatingLabel");
    $[2] = tDetail;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== score || $[5] !== tScore) {
    t4 = tScore(score);
    $[4] = score;
    $[5] = tScore;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setHowItWorksOpen(true);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== tDetail) {
    t6 = tDetail("ratingInfoLabel");
    $[8] = tDetail;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <HowItWorksInfoButton onClick={t5} ariaLabel={t6} />;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== scoreCfg.tone || $[13] !== t1 || $[14] !== t3 || $[15] !== t4 || $[16] !== t7) {
    t8 = <DataCell icon={t1} iconClassName={t2} label={t3} value={t4} action={t7} />;
    $[12] = scoreCfg.tone;
    $[13] = t1;
    $[14] = t3;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== howItWorksOpen) {
    t9 = <ProposalRatingHowItWorksModal open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />;
    $[18] = howItWorksOpen;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== className || $[21] !== t8 || $[22] !== t9) {
    t10 = <div className={className}>{t8}{t9}</div>;
    $[20] = className;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}